import { useState, useEffect } from "react";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  FileText, Download, Users, Clock, Filter,
  Play, Square, LogIn, Loader2, ChevronDown
} from "lucide-react";

const ACTION_COLORS = {
  LOGIN: "bg-blue-100 text-blue-700",
  SHIFT_START: "bg-lime-100 text-lime-700",
  SHIFT_END: "bg-red-100 text-red-700",
  REQUEST_CREATE: "bg-amber-100 text-amber-700",
  DELIVERY: "bg-green-100 text-green-700",
  DEFAULT: "bg-slate-100 text-slate-500",
};

const ACTION_ICONS = {
  LOGIN: LogIn,
  SHIFT_START: Play,
  SHIFT_END: Square,
  DEFAULT: Clock,
};

function formatTs(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso.slice(0, 16);
  }
}

export default function ActivityLogPanel({ hubId }) {
  const { auth, session } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("");
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!hubId || !auth?.jwt) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const params = {};
        if (filterRole) params.role = filterRole;
        const [logData, sumData] = await Promise.all([
          api.getActivityLog(auth.jwt, hubId, params),
          api.getActivitySummary(auth.jwt, hubId),
        ]);
        if (!cancelled) {
          setLogs(logData);
          setSummary(sumData);
        }
      } catch (e) {
        void 0;
      }
      if (!cancelled) setLoading(false);
    }
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [hubId, auth?.jwt, filterRole]);

  const handleDownloadPdf = (section = "all") => {
    if (!auth?.jwt) return;
    const url = api.getReportUrl(hubId, section) + `&token=${auth.jwt}`;
    window.open(url, "_blank");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="activity-log-panel">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
        data-testid="activity-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center">
            <FileText className="w-4 h-4 text-lime-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">{t("crew_activity_log")}</h3>
            {summary && (
              <p className="text-[10px] text-slate-400 font-bold tracking-wide">
                {summary.active_crew} {t("active_label")} / {summary.logins_today} {t("logins_today")} / {summary.total_today} {t("events_today")}
              </p>
            )}
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-200">
          {/* Toolbar */}
          <div className="flex items-center gap-2 p-3 bg-slate-50 border-b border-slate-200">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              data-testid="activity-role-filter"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-1.5 outline-none"
            >
              <option value="">{t("all_roles")}</option>
              <option value="planner">{t("role_planner")}</option>
              <option value="driver">{t("role_driver")}</option>
              <option value="machinist">{t("role_machinist")}</option>
            </select>
            <div className="flex-1" />

            {/* PDF Downloads */}
            <button
              data-testid="download-pdf-all"
              onClick={() => handleDownloadPdf("all")}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all"
            >
              <Download className="w-3.5 h-3.5" /> {t("full_pdf")}
            </button>
            <button
              data-testid="download-pdf-activity"
              onClick={() => handleDownloadPdf("activity")}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-slate-700 text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all"
            >
              <Download className="w-3.5 h-3.5" /> {t("activity_pdf")}
            </button>
          </div>

          {/* Active crew summary */}
          {summary?.active_crew_members?.length > 0 && (
            <div className="px-4 py-3 bg-lime-50 border-b border-lime-200">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-lime-600" />
                <span className="text-[10px] font-black text-lime-700 uppercase tracking-wider">{t("active_crew_now")}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {summary.active_crew_members.map((m, i) => (
                  <span key={i} className="px-2.5 py-1 bg-lime-200 text-lime-800 rounded-lg text-[10px] font-bold">
                    {m.display_name} ({m.role})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Log entries */}
          <div className="max-h-80 overflow-y-auto" data-testid="activity-log-list">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-xs text-slate-400 font-bold uppercase">{t("no_activity_yet")}</p>
              </div>
            ) : (
              logs.map((log, i) => {
                const color = ACTION_COLORS[log.action] || ACTION_COLORS.DEFAULT;
                const Icon = ACTION_ICONS[log.action] || ACTION_ICONS.DEFAULT;
                return (
                  <div key={log.id || i} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-900 truncate">{log.display_name || log.user_email}</span>
                        <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${color}`}>{log.action}</span>
                      </div>
                      {log.detail && <p className="text-[10px] text-slate-400 truncate">{log.detail}</p>}
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono whitespace-nowrap">{formatTs(log.timestamp)}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
